import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { TextPairing } from '@components/TextPairing';
import { Text } from '@components/typography';
import { Box } from '@components/Box';
import { Columns } from '@components/Columns';
import { CloudImage } from '@components/CloudImage';
import { Layout, Seo } from '@layout';
import { AbcSection } from '@sections/shared/AbcSection';
import styled from 'styled-components';
import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { getRootUrl } from '@utils/urlHelpers.util';
import { useTranslation } from 'react-i18next';

const WebsiteImage = styled(CloudImage)`
  ${themeMediaQuery('below_sm')} {
    height: auto;
    max-width: 100%;
  }
`;

const DesktopImage = styled(CloudImage)`
  width: 100%;
  height: auto;
`;

export const MobileBox = styled(Box)`
  position: relative;

  ${themeMediaQuery('below_sm')} {
    width: 100%;
  }
`;

const MobileImage = styled(CloudImage)`
  ${themeMediaQuery('below_sm')} {
    height: auto;
    max-width: 100%;
  }
`;

const MobileContent = styled(CloudImage)`
  position: absolute;
  left: 24px;
  top: 52px;
  border-radius: 30px;

  ${themeMediaQuery('below_sm')} {
    top: 4%;
    left: 7%;
    height: 84%;
    max-width: 90%;
  }
`;

const EventWrap = styled(Box)`
  padding: ${themeSpace(12)} ${themeSpace(0)};

  ${themeMediaQuery('below_sm')} {
    padding: ${themeSpace(8)} ${themeSpace(0)};
  }
`;

const SiteTextPairing = styled(TextPairing)`
  a {
    color: ${themeColor('purple4')};
    text-decoration: underline;
  }
`;

const Demo = () => {
  const { t } = useTranslation('demo');
  const app = t('app');
  const firstCouple = t('firstCouple');
  const seoText = t('seoText');
  const website = t('website');

  return (
    <Layout>
      <Seo title={seoText} canonicalURL={'https://withjoy.com/demo/'} />
      <Box paddingY={[10, 10, 12]} paddingX={[5, 5, 11]}>
        <div style={{ maxWidth: '610px', margin: 'auto' }}>
          <TextPairing
            title={firstCouple.title}
            body={firstCouple.content}
            titleVariant={'hed72Hero'}
            bodyVariant="dek20"
            alignment="center"
          />
        </div>
      </Box>
      <Box paddingBottom={[5, 5, 11]} style={{ display: 'flex', justifyContent: 'center' }}>
        <WebsiteImage height="680px" src="demo/joy_sample_website.jpg" alt="joy website" />
      </Box>
      <Box backgroundColor="yellow1" paddingY={[10, 10, 12]} paddingX={[5, 5, 11]}>
        <Columns>
          <Columns.Column width="half">
            <Box paddingRight={[5, 5, 13]}>
              <SiteTextPairing
                title={website.title}
                body={ReactHtmlParser(website.content)}
                titleVariant={'hed72Hero'}
                bodyVariant="dek20"
              />
            </Box>
          </Columns.Column>
          <Columns.Column width="half">
            <Box paddingTop="120px">
              <TextPairing title={website.eventPassword} titleVariant="calloutHed32" body="AmyPlusDave" bodyVariant="dek20" />
            </Box>
          </Columns.Column>
        </Columns>
        <Columns>
          <Columns.Column width="half">
            <Box paddingTop={[5, 5, 10]}>
              <a target="_blank" rel="noopener noreferrer" href={getRootUrl('/amyplusdave')}>
                <DesktopImage alt={website.altTextSinglePage} src="demo/templates/desktop_aloha.png" optimization="aggressive" />
              </a>
              <Text tagName="p" variant="featureHed20" textAlign="center">
                {website.singlePage}
              </Text>
            </Box>
          </Columns.Column>
          <Columns.Column width="half">
            <Box paddingTop={[5, 5, 10]}>
              <a target="_blank" rel="noopener noreferrer" href={getRootUrl('/amyanddave')}>
                <DesktopImage alt={website.altTextMultiPage} src="demo/templates/desktop_brannan.png" optimization="aggressive" />
              </a>
              <Text tagName="p" variant="featureHed20" textAlign="center">
                {website.multiPage}
              </Text>
            </Box>
          </Columns.Column>
        </Columns>
      </Box>

      <Box backgroundColor="blue2" paddingY={[10, 10, 12]} paddingX={[5, 5, 11]}>
        <Columns>
          <Columns.Column width="half">
            <Box paddingRight={[5, 5, 13]}>
              <TextPairing title={app.title} id="downloadTheApp" body={app.content} titleVariant={'hed72Hero'} bodyVariant="dek20" />
            </Box>
            <MobileBox paddingY={[4, 4, 8]}>
              <MobileImage alt="Mobile application preview" src="common/phone-frames/iphone_blue2.svg" height="838px" />
              <MobileContent alt="Mobile application content" src="demo/iphone_amyplusdave.jpg" height="739px" />
            </MobileBox>
          </Columns.Column>
          <Columns.Column width="half">
            <EventWrap>
              <TextPairing title={app.eventPassword} titleVariant="calloutHed32" body="AmyPlusDave" bodyVariant="dek20" />
            </EventWrap>

            <TextPairing title={app.download} titleVariant="calloutHed32" body={app.downloadContent} bodyVariant="dek20" />
            <Box paddingY={[4, 4, 8]}>
              <a target="_blank" rel="noopener noreferrer" href="https://itunes.apple.com/app/joy-your-wedding-app/id994411720?mt=8">
                <CloudImage
                  alt={app.altTextAppStore}
                  src="common/buttons/appstorenew.png"
                  optimization="aggressive"
                  width="168"
                  height="48"
                />
              </a>
            </Box>
            <Box>
              <a target="_blank" rel="noopener noreferrer" href="https://play.google.com/store/apps/details?id=com.withjoy.joy">
                <CloudImage
                  alt={app.altTextGooglePlay}
                  src="common/buttons/google-play-badge.png"
                  optimization="aggressive"
                  width="168"
                  height="48"
                />
              </a>
            </Box>
          </Columns.Column>
        </Columns>
      </Box>
      <AbcSection />
    </Layout>
  );
};

export default Demo;
